import type { ReactNode } from 'react';

import { WordCloud } from '../../../chrome/chart';
import type { WordCloudTone } from '../../../chrome/chart/WordCloud';
import { count } from '../../../chrome/measure/format';
import { QueryStates } from '../../../chrome/state';
import { getLocale } from '../../../i18n';
import { useCampaignTerms, useTerms, type Window } from '../api/queries';
import type { CommentTermRow } from '../api/schemas';
import { st2, st2Plural } from './messages';
import { SentimentCard } from './SentimentCard';

const TONE: Record<CommentTermRow['stance'], WordCloudTone> = {
  supportive: 'positive',
  hostile: 'negative',
  neutral: 'neutral',
  off_topic: 'neutral',
};

/** The hover line: how often the term was used and, when the route splits it,
 *  how those uses lean. */
export function termDetail(row: CommentTermRow): string {
  const locale = getLocale();
  const mentions = st2Plural('terms.mentions.other', row.count, { n: count(row.count, locale) });
  if (row.supportive === undefined || row.neutral === undefined || row.hostile === undefined) {
    return `${row.term} · ${mentions}`;
  }
  return st2('terms.detail', {
    term: row.term,
    mentions,
    supportive: count(row.supportive, locale),
    neutral: count(row.neutral, locale),
    hostile: count(row.hostile, locale),
  });
}

export function TermsView(props: { rows: readonly CommentTermRow[] }): ReactNode {
  return (
    <WordCloud
      label={st2('terms.label')}
      terms={props.rows.map((row) => ({
        term: row.term,
        weight: row.count,
        tone: TONE[row.stance],
        detail: termDetail(row),
      }))}
    />
  );
}

/** A chosen post reads its own terms; every other scope reads the whole
 *  campaign's. With no term to draw the card is left out of the page. */
export function TermsCard(props: {
  campaignId: string;
  tenantId: string;
  postId: string;
  window: Window;
}): ReactNode {
  // An empty campaign id keeps the campaign read disabled while a post is chosen.
  const campaign = useCampaignTerms(props.postId === '' ? props.campaignId : '', props.tenantId, {
    window: props.window,
  });
  const post = useTerms(props.campaignId, props.tenantId, props.postId, { window: props.window });
  const query = props.postId === '' ? campaign : post;
  if (query.status === 'success' && query.data.rows.length === 0) return null;
  return (
    <SentimentCard title={st2('terms.title')}>
      <QueryStates query={query}>{(data) => <TermsView rows={data.rows} />}</QueryStates>
    </SentimentCard>
  );
}
